import * as z from 'zod'
import {
    ConvertibleListItemAllConvertibles,
    ConvertibleListItemByCompany,
    ConvertibleSingleItem,
    DConvertibleListItemAllConvertibles,
    DConvertibleListItemByCompany,
    DConvertibleSingleItem,
} from '../ConvertibleEntity.js'
import { DeleteApiContract, GetApiContract, PatchApiContract, PostApiContract } from './ApiContract.js'
import { schemaBoolish } from './zodUtils.js'
import { advisorSchema, linkedIssueSchema } from './utils.js'
import { putInvestorSchema } from './InvestorsApiContract.js'
import { putSourceSchema } from './SourceApiContract.js'
import { ConvertibleAdvisorType } from '../AdvisorEntity.js'

export interface GetConvertibleSingleItemRequest extends GetApiContract {
    Response: DConvertibleSingleItem
    ResponseJson: ConvertibleSingleItem
}

export interface GetConvertiblesByCompanyRequest extends GetApiContract {
    Response: Array<DConvertibleListItemByCompany>
    ResponseJson: Array<ConvertibleListItemByCompany>
}

export interface GetAllConvertiblesRequest extends GetApiContract {
    Response: Array<DConvertibleListItemAllConvertibles>
    ResponseJson: Array<ConvertibleListItemAllConvertibles>
}

export const postConvertibleSchema = z.object({
    advisors: z
        .array(
            advisorSchema.extend({
                type: z.union([
                    z.string<ConvertibleAdvisorType>('financial_advisor'),
                    z.string<ConvertibleAdvisorType>('legal_advisor'),
                ]),
            }),
        )
        .nullish(),
    annualInterest: z.union([z.string(), z.number()]).nullish(),
    arrangementFee: z.union([z.string(), z.number()]).nullish(),
    completed: schemaBoolish.nullish(),
    convertAnytime: schemaBoolish.nullish(),
    creditLine: z.number().nullish(),
    externalComment: z.string().nullish(),
    fixedConversionPrice: z.union([z.string(), z.number()]).nullish(),
    internalComment: z.string().nullish(),
    investors: z.array(putInvestorSchema).nullish(),
    maturityDate: z.string().nullish(),
    monthlyInterest: z.union([z.string(), z.number()]).nullish(),
    noInvestorsNamed: schemaBoolish.nullish(),
    noncashConsideration: z.string().nullish(),
    priceMax: z.union([z.string(), z.number()]).nullish(),
    priceMin: z.union([z.string(), z.number()]).nullish(),
    shareId: z.string(),
    sources: z.array(putSourceSchema).nullish(),
    stibor: schemaBoolish.nullish(),
    vwap: schemaBoolish.nullish(),
    vwapDays: z.number().nullish(),
    vwapDiscount: z.string().nullish(),
})

export interface PostConvertibleRequest extends PostApiContract {
    Body: z.infer<typeof postConvertibleSchema>
    Response: DConvertibleSingleItem
    ResponseJson: ConvertibleSingleItem
}

export const patchConvertibleSchema = postConvertibleSchema.extend({
    convertibleActions: z.array(
        z.object({
            actionType: z.string().optional(),
            conversionPrice: z.union([z.string(), z.number()]).nullish(),
            convertedShares: z.number().nullish(),
            loanAmount: z.union([z.string(), z.number()]).nullish(),
            recordDate: z.string().optional(),
            repayment: z.union([z.string(), z.number()]).nullish(),
        }),
    ),
    linkedIssues: z.array(linkedIssueSchema).nullish(),
})

export type PatchConvertibleResponse = z.infer<typeof patchConvertibleSchema> & { id: string }

export interface PatchConvertibleRequest extends PatchApiContract {
    Body: z.infer<typeof patchConvertibleSchema>
    Response: PatchConvertibleResponse
    ResponseJson: PatchConvertibleResponse
}

export interface DeleteConvertibleRequest extends DeleteApiContract {
    Response: Record<never, never>
    ResponseJson: Record<never, never>
}
